import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { useApi } from "../../../hooks/useApi";
import api from "../../../utils/api";
import "./Products.scss";

const ProductNew = () => {
  const history = useHistory();
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    price: "",
    regularPrice: "",
    salePrice: "",
    stockQuantity: "0",
    imageUrl: "",
    categoryId: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Fetch categories for dropdown
  const { data: categoriesData } = useApi(() => api.getAdminCategories(), []);
  const categories = categoriesData?.categories || [];

  // Auto-generate slug from name
  useEffect(() => {
    if (formData.name && !formData.slug) {
      const slug = formData.name
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.name]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setSubmitError("");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Product name is required";
    }

    if (!formData.slug.trim()) {
      newErrors.slug = "Slug is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = "Slug can only contain lowercase letters, numbers, and hyphens";
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = "Valid price is required";
    }

    if (formData.regularPrice && parseFloat(formData.regularPrice) <= 0) {
      newErrors.regularPrice = "Regular price must be greater than 0";
    }

    if (formData.salePrice) {
      const salePrice = parseFloat(formData.salePrice);
      const regularPrice = parseFloat(formData.regularPrice || formData.price);
      if (salePrice <= 0) {
        newErrors.salePrice = "Sale price must be greater than 0";
      } else if (salePrice >= regularPrice) {
        newErrors.salePrice = "Sale price must be less than regular price";
      }
    }

    if (formData.stockQuantity && parseInt(formData.stockQuantity) < 0) {
      newErrors.stockQuantity = "Stock quantity cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const productData = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: formData.description.trim() || null,
        price: parseFloat(formData.price),
        regularPrice: formData.regularPrice ? parseFloat(formData.regularPrice) : parseFloat(formData.price),
        salePrice: formData.salePrice ? parseFloat(formData.salePrice) : null,
        stockQuantity: parseInt(formData.stockQuantity) || 0,
        imageUrl: formData.imageUrl.trim() || null,
        categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
      };

      await api.createProduct(productData);
      history.push("/admin/products");
    } catch (error) {
      console.error("Error creating product:", error);
      setSubmitError(error.message || "Failed to create product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-products">
      <div className="products-header">
        <h2>Add New Product</h2>
        <Link to="/admin/products" className="btn-secondary">
          Back to Products
        </Link>
      </div>

      <div className="product-form-container">
        <form onSubmit={handleSubmit} className="product-form">
          {submitError && (
            <div className="form-error-message">{submitError}</div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">
                Product Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? "error" : ""}
                placeholder="Enter product name"
                required
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="slug">
                Slug <span className="required">*</span>
              </label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className={errors.slug ? "error" : ""}
                placeholder="product-slug"
                required
              />
              {errors.slug && <span className="field-error">{errors.slug}</span>}
              <small className="form-hint">Auto-generated from name, but you can edit it</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="5"
              placeholder="Enter product description"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">
                Price <span className="required">*</span>
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className={errors.price ? "error" : ""}
                placeholder="0.00"
                step="0.01"
                min="0"
                required
              />
              {errors.price && <span className="field-error">{errors.price}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="regularPrice">Regular Price</label>
              <input
                type="number"
                id="regularPrice"
                name="regularPrice"
                value={formData.regularPrice}
                onChange={handleChange}
                className={errors.regularPrice ? "error" : ""}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
              {errors.regularPrice && <span className="field-error">{errors.regularPrice}</span>}
              <small className="form-hint">Leave empty to use price as regular price</small>
            </div>

            <div className="form-group">
              <label htmlFor="salePrice">Sale Price</label>
              <input
                type="number"
                id="salePrice"
                name="salePrice"
                value={formData.salePrice}
                onChange={handleChange}
                className={errors.salePrice ? "error" : ""}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
              {errors.salePrice && <span className="field-error">{errors.salePrice}</span>}
              <small className="form-hint">Set to enable sale pricing</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="stockQuantity">Stock Quantity</label>
              <input
                type="number"
                id="stockQuantity"
                name="stockQuantity"
                value={formData.stockQuantity}
                onChange={handleChange}
                className={errors.stockQuantity ? "error" : ""}
                placeholder="0"
                min="0"
              />
              {errors.stockQuantity && <span className="field-error">{errors.stockQuantity}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="categoryId">Category</label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
              >
                <option value="">Select a category (optional)</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="imageUrl">Image URL</label>
            <input
              type="url"
              id="imageUrl"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
            />
            <small className="form-hint">Enter the full URL to the product image</small>
            {formData.imageUrl && (
              <div className="image-preview">
                <img src={formData.imageUrl} alt="Preview" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? "Creating..." : "Create Product"}
            </button>
            <Link to="/admin/products" className="btn-secondary">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductNew;

